import React, { useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { ModalBody, Button } from 'reactstrap';
import { FormattedMessage } from 'react-intl';
import Checkbox from '../../../components/Checkbox';
import { AccessibleModal } from '../../../components/AccessibleModal';
import './index.css';
import messages from './messages';

function RoomScanModel({ onRoomScanComplete, isOpen }) {
  const [pageChanger, setPageChanger] = useState(false);
  const [isCheckBox1Checked, setIsCheckBox1Checked] = useState(false);
  const [isCheckBox2Checked, setIsCheckBox2Checked] = useState(false);
  const [isCheckBox3Checked, setIsCheckBox3Checked] = useState(false);
  const [isCheckBox4Checked, setIsCheckBox4Checked] = useState(false);
  const [isCheckBox5Checked, setIsCheckBox5Checked] = useState(false);
  const [isCheckBox6Checked, setIsCheckBox6Checked] = useState(false);
  const [isCheckBox7Checked, setIsCheckBox7Checked] = useState(false);
  const [modalButton, setModalButton] = useState(false);
  const stepHeadingRef = useRef(null);

  useLayoutEffect(() => {
    if (isOpen && pageChanger && stepHeadingRef.current) {
      stepHeadingRef.current.focus();
    }
  }, [isOpen, pageChanger]);

  const checkboxNames = {
    checkbox1: 'checkbox1',
    checkbox7: 'checkbox7',
    checkbox2: 'checkbox2',
    checkbox3: 'checkbox3',
    checkbox4: 'checkbox4',
    checkbox5: 'checkbox5',
    checkbox6: 'checkbox6',
  };

  function onCheckboxChanged(isChecked, name) {
    switch (name) {
      case checkboxNames.checkbox1: {
        setIsCheckBox1Checked(isChecked);
        break;
      }
      case checkboxNames.checkbox2: {
        setIsCheckBox2Checked(isChecked);
        break;
      }
      case checkboxNames.checkbox3: {
        setIsCheckBox3Checked(isChecked);
        break;
      }
      case checkboxNames.checkbox4: {
        setIsCheckBox4Checked(isChecked);
        break;
      }
      case checkboxNames.checkbox5: {
        setIsCheckBox5Checked(isChecked);
        break;
      }
      case checkboxNames.checkbox6: {
        setIsCheckBox6Checked(isChecked);
        break;
      }
      case checkboxNames.checkbox7: {
        setIsCheckBox7Checked(isChecked);
        break;
      }
      default: {
        break;
      }
    }
  }

  const isButtonEnabled =
    isCheckBox1Checked &&
    isCheckBox2Checked &&
    isCheckBox3Checked &&
    isCheckBox4Checked &&
    isCheckBox5Checked &&
    isCheckBox6Checked &&
    isCheckBox7Checked &&
    modalButton;

  const handleButtonClick = () => {
    setPageChanger(!pageChanger);
    setTimeout(() => {
      setModalButton(true);
    }, 30000);
  };

  const handleChecklistKeyDown = event => {
    if (event.key !== 'Tab') return;
    const controls = event.currentTarget.querySelectorAll(
      'input:not(:disabled), button:not(:disabled)',
    );
    const firstControl = controls[0];
    const lastControl = controls[controls.length - 1];
    if (event.shiftKey && event.target === firstControl) {
      event.preventDefault();
      event.stopPropagation();
      lastControl.focus();
    } else if (!event.shiftKey && event.target === lastControl) {
      event.preventDefault();
      event.stopPropagation();
      firstControl.focus();
    }
  };

  return (
    <div>
      {pageChanger ? (
        <AccessibleModal
          isOpen={isOpen}
          modalClassName="room-scan-modal"
          labelledBy="room-scan-heading"
        >
          <h2
            id="room-scan-heading"
            ref={stepHeadingRef}
            tabIndex={-1}
            className="room-scan-modal-header"
          >
            <FormattedMessage {...messages.heading2} />
          </h2>
          <p className="room-scan-modal-warning">
            <strong>
              <FormattedMessage {...messages.warning} />
            </strong>
          </p>
          <hr aria-hidden="true" className="room-scan-modal-hr" />
          <ModalBody
            className="room-scan-modal-body"
            onKeyDown={handleChecklistKeyDown}
          >
            <div id="room-scan-instructions">
              <p className="room-scan-modal-score-warning">
                <em>{messages.instructions2.heading}</em>
              </p>
              <div
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: messages.instructions2.defaultMessage,
                }}
              />
            </div>
            {/* render room scanning checkboxes */}
            {Object.keys(checkboxNames).map(checkbox => (
              <div
                key={checkbox}
                className="room-scan-modal-checklist-item-container"
              >
                <Checkbox
                  id={`room-scan-${checkbox}`}
                  disabled={false}
                  onCheckChanged={checked =>
                    onCheckboxChanged(checked, checkboxNames[checkbox])
                  }
                  checked={false}
                  labelClassName="room-scan-modal-checklist-text"
                  labelTextContent={
                    messages[`checkboxText${checkbox.replace('checkbox', '')}`]
                      .defaultMessage
                  }
                />
              </div>
            ))}

            <p
              id="room-scan-completion-warning"
              className="room-scan-modal-score-warning"
            >
              <em>
                <FormattedMessage {...messages.completionWarning} />
              </em>
            </p>
            <div
              className={
                isButtonEnabled
                  ? 'room-scan-modal-button-container-enabled'
                  : 'room-scan-modal-button-container-disabled'
              }
            >
              <Button
                disabled={!isButtonEnabled}
                onClick={isButtonEnabled ? onRoomScanComplete : null}
                aria-describedby="room-scan-instructions room-scan-completion-warning"
                aria-disabled={!isButtonEnabled}
              >
                <FormattedMessage {...messages.buttonText2} />
              </Button>
            </div>
          </ModalBody>
        </AccessibleModal>
      ) : (
        <AccessibleModal
          isOpen={isOpen}
          modalClassName="room-scan-modal"
          labelledBy="room-scan-heading"
        >
          <h2 id="room-scan-heading" className="room-scan-modal-header">
            <FormattedMessage {...messages.heading} />
          </h2>
          <p className="room-scan-modal-warning">
            <strong>
              <FormattedMessage {...messages.warning} />
            </strong>
          </p>
          <hr aria-hidden="true" className="room-scan-modal-hr" />
          <ModalBody className="room-scan-modal-body">
            <div>
              <p className="room-scan-modal-score-warning">
                <em>{messages.instructions.heading}</em>
              </p>
              <div
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: messages.instructions.defaultMessage,
                }}
              />
            </div>
            <div className="room-scan-modal-button-container-enabled">
              <Button onClick={handleButtonClick}>
                <FormattedMessage {...messages.buttonText} />
              </Button>
            </div>
          </ModalBody>
        </AccessibleModal>
      )}
    </div>
  );
}

RoomScanModel.propTypes = {
  onRoomScanComplete: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
};

export default RoomScanModel;
